import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMobileOptimization } from '../hooks/useMobileOptimization';
import PageTemplate from '../components/PageTemplate';
import PersonBreadcrumb from '../components/PersonBreadcrumb';
import RelationshipNavigator from '../components/RelationshipNavigator';
import AncestorPedigreeView from '../components/AncestorPedigreeView';
import { usePersonNavigation } from '../contexts/PersonNavigationContext';
import './PersonDetailsPage.css';

const PersonDetailsPage = () => {
  const { personId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isTouchDevice } = useMobileOptimization();
  const { pushPerson, navigateToIndex } = usePersonNavigation();

  // Get person data and gedcom data from location state or load from storage
  const [person, setPerson] = useState(location.state?.person || null);
  const [gedcomData, setGedcomData] = useState(location.state?.gedcomData || null);
  const [pedigreeExpanded, setPedigreeExpanded] = useState(true);

  // Update person and gedcomData when navigating between persons (same route, different params)
  useEffect(() => {
    if (location.state?.person) {
      setPerson(location.state.person);
    }
    if (location.state?.gedcomData) {
      setGedcomData(location.state.gedcomData);
    }
  }, [location.state, personId]);

  // Load data from localStorage if not provided in state
  useEffect(() => {
    if (!person || !gedcomData || (person.id !== personId)) {
      // If we have gedcomData but person doesn't match the URL, look up the person
      if (gedcomData && personId && gedcomData.individuals && gedcomData.individuals[personId]) {
        setPerson(gedcomData.individuals[personId]);
        return;
      }
      // Otherwise try localStorage
      try {
        const storedGedcomData = localStorage.getItem('gedcomData');
        if (storedGedcomData) {
          const parsedData = JSON.parse(storedGedcomData);
          setGedcomData(parsedData);

          if (personId && parsedData.individuals && parsedData.individuals[personId]) {
            const foundPerson = parsedData.individuals[personId];
            setPerson(foundPerson);
          }
        }
      } catch (error) {
        console.error('Error loading person data:', error);
      }
    }
  }, [personId, person, gedcomData]);

  // Persist gedcomData to localStorage when received via navigation state
  // This ensures back button and direct URL access can still load data
  useEffect(() => {
    if (gedcomData) {
      try {
        localStorage.setItem('gedcomData', JSON.stringify(gedcomData));
      } catch (error) {
        // localStorage may be full or unavailable — non-critical
        console.warn('Could not persist gedcomData to localStorage:', error);
      }
    }
  }, [gedcomData]);

  // Push current person to breadcrumb trail when person changes
  useEffect(() => {
    if (person) {
      pushPerson({ id: person.id, name: person.name || 'Unknown' });
    }
  }, [person, pushPerson]);

  if (!person || !gedcomData) {
    return (
      <PageTemplate
        title="Person Not Found"
        icon="fas fa-exclamation-triangle"
        maxWidth="default"
      >
        <div className="section">
          <div className="person-not-found">
            <p>Sorry, the person you're looking for doesn't exist or the family tree data is not available.</p>
            <button
              className="cta-btn primary"
              onClick={() => navigate('/family-tree')}
            >
              <i className="fas fa-arrow-left"></i>
              Back to Family Tree
            </button>
          </div>
        </div>
      </PageTemplate>
    );
  }

  const calculateAge = (birth, death) => {
    if (!birth) return null;
    const birthYear = parseInt(birth.match(/\d{4}/)?.[0]);
    if (!birthYear) return null;
    const endYear = death ? parseInt(death.match(/\d{4}/)?.[0]) : new Date().getFullYear();
    return endYear - birthYear;
  };

  const handlePersonSelect = (selectedPersonId) => {
    const selectedPerson = gedcomData.individuals[selectedPersonId];
    if (selectedPerson) {
      navigate(`/person/${selectedPersonId}`, {
        state: { person: selectedPerson, gedcomData }
      });
    }
  };

  const handleBreadcrumbNavigate = (breadcrumbPerson, index) => {
    // Truncate trail to the clicked entry (removes all entries after it per Req 3.7)
    if (typeof index === 'number') {
      navigateToIndex(index);
    }
    const targetPerson = gedcomData.individuals[breadcrumbPerson.id];
    if (targetPerson) {
      navigate(`/person/${breadcrumbPerson.id}`, {
        state: { person: targetPerson, gedcomData }
      });
    }
  };

  const age = calculateAge(person.birth, person.death);

  // Helper: check if a string field has data
  const hasData = (value) => value && value.trim && value.trim() !== '';

  // Get marriage info from families
  const getMarriages = () => {
    const marriages = [];
    if (!person.families || person.families.length === 0) return marriages;

    person.families.forEach((familyId) => {
      const family = gedcomData.families[familyId];
      if (!family) return;

      const spouseId = family.husband === person.id ? family.wife : family.husband;
      const spouse = spouseId ? gedcomData.individuals[spouseId] : null;

      if (spouse || family.marriage || family.marriagePlace) {
        marriages.push({
          spouseName: spouse ? spouse.name : null,
          spouseId: spouseId,
          date: family.marriage || '',
          place: family.marriagePlace || ''
        });
      }
    });

    return marriages;
  };

  const marriages = getMarriages();

  // Determine which sections have data
  const hasBirth = hasData(person.birth) || hasData(person.birthPlace);
  const hasDeath = hasData(person.death) || hasData(person.deathPlace);
  const hasVitals = hasBirth || hasDeath;
  const hasOccupation = hasData(person.occupation);
  const hasEducation = hasData(person.education);
  const hasReligion = hasData(person.religion);
  const hasMarriages = marriages.length > 0;
  const hasEvents = hasMarriages || hasOccupation || hasEducation || hasReligion;
  const hasNote = hasData(person.note);
  const hasSources = person.sources && person.sources.length > 0;
  const hasCustomTags = person.customTags && Object.keys(person.customTags).length > 0;

  const pageActions = [
    {
      label: 'Back to Family Tree',
      icon: 'fas fa-arrow-left',
      className: 'cta-btn secondary',
      onClick: () => navigate('/family-tree', { state: { gedcomData } })
    }
  ];

  return (
    <PageTemplate
      title="Person Details"
      description={person.name || 'Unknown Person'}
      icon="fas fa-user"
      maxWidth="wide"
      actions={pageActions}
      className={`person-details-page ${isMobile() ? 'mobile-optimized' : ''} ${isTouchDevice() ? 'touch-optimized' : ''}`}
    >
      {/* Breadcrumb Navigation */}
      <PersonBreadcrumb onNavigate={handleBreadcrumbNavigate} />

      {/* Person Header */}
      <div className="section">
        <div className="person-header">
          <div className={`person-avatar ${person.sex === 'M' ? 'male' : 'female'}`}>
            <i className={`fas ${person.sex === 'M' ? 'fa-mars' : 'fa-venus'}`}></i>
          </div>
          <div className="person-info">
            <h2>{person.name || 'Unknown Name'}</h2>
            <div className="person-dates">
              {hasData(person.birth) && (
                <span><i className="fas fa-birthday-cake"></i> Born: {person.birth}</span>
              )}
              {hasData(person.death) && (
                <span><i className="fas fa-cross"></i> Died: {person.death}</span>
              )}
              {age && <span><i className="fas fa-hourglass-half"></i> Age: {age} years</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Vital Information Section */}
      {hasVitals && (
        <div className="section">
          <div className="detail-section">
            <h3><i className="fas fa-heartbeat"></i> Vital Information</h3>
            <div className="detail-items">
              {hasData(person.birth) && (
                <div className="detail-item">
                  <strong>Birth Date:</strong>
                  <span>{person.birth}</span>
                </div>
              )}
              {hasData(person.birthPlace) && (
                <div className="detail-item">
                  <strong>Birth Place:</strong>
                  <span>{person.birthPlace}</span>
                </div>
              )}
              {hasData(person.death) && (
                <div className="detail-item">
                  <strong>Death Date:</strong>
                  <span>{person.death}</span>
                </div>
              )}
              {hasData(person.deathPlace) && (
                <div className="detail-item">
                  <strong>Death Place:</strong>
                  <span>{person.deathPlace}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Events Section */}
      {hasEvents && (
        <div className="section">
          <div className="person-details-grid">
            {/* Marriage Events */}
            {hasMarriages && (
              <div className="detail-section">
                <h3><i className="fas fa-ring"></i> Marriage</h3>
                <div className="detail-items">
                  {marriages.map((marriage, index) => (
                    <div key={index} className="detail-item">
                      {marriage.spouseName && (
                        <span className="marriage-entry">
                          <button
                            className="relative-link"
                            onClick={() => handlePersonSelect(marriage.spouseId)}
                            type="button"
                          >
                            {marriage.spouseName}
                          </button>
                          {hasData(marriage.date) && (
                            <span className="marriage-date"> (m. {marriage.date})</span>
                          )}
                        </span>
                      )}
                      {hasData(marriage.place) && (
                        <span className="marriage-place">{marriage.place}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Occupation - independent section */}
            {hasOccupation && (
              <div className="detail-section">
                <h3><i className="fas fa-briefcase"></i> Occupation</h3>
                <div className="detail-items">
                  <div className="detail-item">
                    <span>{person.occupation}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Education - independent section */}
            {hasEducation && (
              <div className="detail-section">
                <h3><i className="fas fa-graduation-cap"></i> Education</h3>
                <div className="detail-items">
                  <div className="detail-item">
                    <span>{person.education}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Religion - independent section */}
            {hasReligion && (
              <div className="detail-section">
                <h3><i className="fas fa-pray"></i> Religion</h3>
                <div className="detail-items">
                  <div className="detail-item">
                    <span>{person.religion}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Relationship Navigator */}
      <div className="section">
        <RelationshipNavigator
          person={person}
          gedcomData={gedcomData}
          onPersonSelect={handlePersonSelect}
        />
      </div>

      {/* Ancestor Pedigree View - Collapsible */}
      <div className="section">
        <div className="detail-section pedigree-section">
          <h3
            className="collapsible-header"
            onClick={() => setPedigreeExpanded(!pedigreeExpanded)}
            role="button"
            tabIndex={0}
            aria-expanded={pedigreeExpanded}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setPedigreeExpanded(!pedigreeExpanded);
              }
            }}
          >
            <i className="fas fa-sitemap"></i> Ancestor Pedigree
            <i className={`fas fa-chevron-${pedigreeExpanded ? 'up' : 'down'} collapse-icon`}></i>
          </h3>
          {pedigreeExpanded && (
            <div className="pedigree-content">
              <AncestorPedigreeView
                gedcomData={gedcomData}
                personId={person.id}
                onPersonClick={handlePersonSelect}
              />
            </div>
          )}
        </div>
      </div>

      {/* Notes Section */}
      {hasNote && (
        <div className="section">
          <div className="detail-section full-width">
            <h3><i className="fas fa-sticky-note"></i> Notes</h3>
            <div className="person-notes">
              {person.note}
            </div>
          </div>
        </div>
      )}

      {/* Custom Tags Section - after notes, before sources */}
      {hasCustomTags && (
        <div className="section">
          <div className="detail-section full-width">
            <h3><i className="fas fa-tags"></i> Custom Tags</h3>
            <div className="detail-items">
              {Object.entries(person.customTags).map(([tagName, tagValue]) => (
                <div key={tagName} className="detail-item">
                  <strong>{tagName}:</strong>
                  <span>{tagValue}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sources Section */}
      {hasSources && (
        <div className="section">
          <div className="detail-section full-width">
            <h3><i className="fas fa-book"></i> Sources</h3>
            <div className="detail-items">
              {person.sources.map((source, index) => (
                <div key={index} className="detail-item">
                  <span>{source}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </PageTemplate>
  );
};

export default PersonDetailsPage;
